// 수학 출제하기의 '범위 골라 출제' 탭. 과목 → 단원 → 소분류 → 세부유형을 고르고 조건으로 뽑는다.
// 분류표는 수학 사이트 /bank/units/ (서버 api/assignment/math_bank.py). 문항 수는 어디에도 보이지 않는다(2026-11-03 대표).
import React, { useEffect, useMemo, useRef, useState } from "react";

import apiClient from "../../../services/apiClient";
import { MathBankChipList, MathBankField, MathBankFilterFields, toggleInList } from "./MathBankControls";
import MathBankRatioBar, { DEFAULT_DIFFICULTY_RATIO, DIFFICULTY_LIST, ratioForKeyList } from "./MathBankRatioBar";
import { MAX_TOTAL, problemFilterPayloadOf } from "./mathBankShared";

const MathBankRangePanel = ({ filter, onFilterChange, hasPickedProblems, onPicked }) => {
  const [unitData, setUnitData] = useState(null);
  const [subjectCode, setSubjectCode] = useState("");
  const [openCodeList, setOpenCodeList] = useState([]);
  const [unitCodeList, setUnitCodeList] = useState([]);
  const [skillCodeList, setSkillCodeList] = useState([]);
  const [typeCodeList, setTypeCodeList] = useState([]);
  const [difficultyRatio, setDifficultyRatio] = useState(DEFAULT_DIFFICULTY_RATIO);
  // 단원 · 소분류 비율. 두 개 이상 골랐을 때만 막대를 보이고 보낸다. 고른 것이 바뀌면 고르게 다시 나눈다
  const [unitRatio, setUnitRatio] = useState({});
  const [skillRatio, setSkillRatio] = useState({});
  const [kindList, setKindList] = useState([]);
  const [total, setTotal] = useState(20);
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    apiClient
      .get("/api/v1/assignment/math/bank/units", { withAuth: true })
      .then((response) => {
        setUnitData(response.data);
        setSubjectCode(response.data.subjects?.[0]?.code || "");
      })
      .catch((error) => setUnitData({ subjects: [], error: error?.response?.data?.error || "분류표를 불러오지 못했습니다." }));
  }, []);

  const subjectList = useMemo(() => unitData?.subjects || [], [unitData]);
  const subject = subjectList.find((one) => one.code === subjectCode);
  const levelList = useMemo(() => [...new Set(subjectList.map((one) => one.level))], [subjectList]);

  // 고른 범위를 이름으로 보여 주려고 모든 과목의 코드 → (이름, 과목 이름)을 한 번 만들어 둔다
  const rangeNameByCode = useMemo(() => {
    const nameByCode = {};
    subjectList.forEach((one) =>
      (one.units || []).forEach((unit) => {
        nameByCode[unit.code] = { name: unit.name, subjectName: one.name };
        (unit.skills || []).forEach((skill) => {
          nameByCode[skill.code] = { name: skill.name, subjectName: one.name };
          (skill.types || []).forEach((type) => (nameByCode[type.code] = { name: type.name, subjectName: one.name }));
        });
      })
    );
    return nameByCode;
  }, [subjectList]);

  // 단원을 고르면 그 아래 고른 것은 지운다(겹쳐 보내지 않는다). 아래를 고르면 그 단원은 푼다
  const withCode = (code, isChecked) => (list) =>
    isChecked ? (list.includes(code) ? list : [...list, code]) : list.filter((one) => one !== code);
  const setUnitChecked = (unit, isChecked) => {
    const childSkillList = (unit.skills || []).map((skill) => skill.code);
    const childTypeList = (unit.skills || []).flatMap((skill) => (skill.types || []).map((type) => type.code));
    if (isChecked) {
      setSkillCodeList((list) => list.filter((code) => !childSkillList.includes(code)));
      setTypeCodeList((list) => list.filter((code) => !childTypeList.includes(code)));
    }
    setUnitCodeList(withCode(unit.code, isChecked));
  };
  const setSkillChecked = (unit, skill, isChecked) => {
    const childTypeList = (skill.types || []).map((type) => type.code);
    if (isChecked) {
      setUnitCodeList((list) => list.filter((code) => code !== unit.code));
      setTypeCodeList((list) => list.filter((code) => !childTypeList.includes(code)));
    }
    setSkillCodeList(withCode(skill.code, isChecked));
  };
  const setTypeChecked = (unit, skill, type, isChecked) => {
    if (isChecked) {
      setUnitCodeList((list) => list.filter((code) => code !== unit.code));
      setSkillCodeList((list) => list.filter((code) => code !== skill.code));
    }
    setTypeCodeList(withCode(type.code, isChecked));
  };

  // 끌어서 여러 줄 고르기(2026-11-05 대표). 처음 누른 줄을 켜면 지나가는 줄을 다 켜고, 끄면 다 끈다.
  // 대분류 · 소분류 · 세부유형 어느 줄이든 섞어 지나가도 된다. 터치는 화면을 밀어 올리는 것과 겹쳐 네모 칸에서 시작할 때만 끈다
  const rowByCodeRef = useRef({});
  const paintRef = useRef(null);
  const skipNextChangeRef = useRef(false);
  useEffect(() => {
    const handleMove = (event) => {
      const paint = paintRef.current;
      if (!paint) return;
      const rowElement = document.elementFromPoint(event.clientX, event.clientY)?.closest("[data-range-code]");
      const code = rowElement?.getAttribute("data-range-code");
      if (!code || paint.visitedCodeSet.has(code)) return;
      paint.visitedCodeSet.add(code);
      rowByCodeRef.current[code]?.setChecked(paint.isChecked);
    };
    const handleUp = () => {
      if (!paintRef.current) return;
      paintRef.current = null;
      document.body.classList.remove("math-bank-page--painting");
      // 누르고 뗀 뒤 따라오는 click 이 체크를 한 번 더 뒤집지 않게, 그 click 까지만 막는다
      setTimeout(() => (skipNextChangeRef.current = false), 0);
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
    };
  }, []);
  const startPaint = (code) => (event) => {
    if (event.button !== 0) return;
    if (event.pointerType !== "mouse" && event.target.tagName !== "INPUT") return;
    const row = rowByCodeRef.current[code];
    if (!row) return;
    event.preventDefault();
    event.target.releasePointerCapture?.(event.pointerId);
    skipNextChangeRef.current = true;
    paintRef.current = { isChecked: !row.isChecked, visitedCodeSet: new Set([code]) };
    document.body.classList.add("math-bank-page--painting");
    row.setChecked(!row.isChecked);
  };
  const removeRange = (code) => {
    setUnitCodeList((list) => list.filter((one) => one !== code));
    setSkillCodeList((list) => list.filter((one) => one !== code));
    setTypeCodeList((list) => list.filter((one) => one !== code));
  };
  const clearRange = () => {
    setUnitCodeList([]);
    setSkillCodeList([]);
    setTypeCodeList([]);
  };
  const toggleOpen = (code) => setOpenCodeList((list) => toggleInList(list, code));

  const selectedCodeList = [...unitCodeList, ...skillCodeList, ...typeCodeList];
  const shownUnitRatio = ratioForKeyList(unitRatio, unitCodeList);
  const shownSkillRatio = ratioForKeyList(skillRatio, skillCodeList);
  const ratioItemListOf = (codeList) => codeList.map((code) => ({ key: code, label: rangeNameByCode[code]?.name || code }));

  // 과제 제목 기본값: 처음 고른 범위의 과목 이름 + 범위 이름
  const firstRangeTitle = () => {
    const first = rangeNameByCode[selectedCodeList[0]];
    return first ? `${first.subjectName} ${first.name}` : "";
  };

  const pick = async () => {
    if (!selectedCodeList.length) return alert("단원을 골라주세요.");
    if (isBusy) return;
    setIsBusy(true);
    try {
      const response = await apiClient.post(
        "/api/v1/assignment/math/bank/pick",
        {
          units: unitCodeList,
          skills: skillCodeList,
          types: typeCodeList,
          total,
          difficulty_ratio: difficultyRatio,
          ...(unitCodeList.length > 1 ? { unit_ratio: shownUnitRatio } : {}),
          ...(skillCodeList.length > 1 ? { skill_ratio: shownSkillRatio } : {}),
          kinds: kindList,
          ...problemFilterPayloadOf(filter),
        },
        { withAuth: true }
      );
      if (!response.data.problem_list.length) return alert("조건에 맞는 문제가 없습니다.");
      onPicked(response.data.problem_list, firstRangeTitle());
      if (response.data.count < response.data.requested) {
        alert(`조건에 맞는 문제가 ${response.data.count}개뿐이라 ${response.data.count}문제만 담았습니다.`);
      }
    } catch (error) {
      alert(error?.response?.data?.error || "문제를 뽑지 못했습니다.");
    } finally {
      setIsBusy(false);
    }
  };

  const renderCheckRow = ({ code, name, isChecked, setChecked, childCount, depth }) => {
    rowByCodeRef.current[code] = { isChecked, setChecked };
    return (
    <div className={`math-bank-page__tree-row math-bank-page__tree-row--depth-${depth}`} data-range-code={code}>
      {childCount > 0 ? (
        <button
          type="button"
          className={`math-bank-page__chevron${openCodeList.includes(code) ? " math-bank-page__chevron--open" : ""}`}
          aria-label={openCodeList.includes(code) ? "접기" : "펼치기"}
          aria-expanded={openCodeList.includes(code)}
          onClick={() => toggleOpen(code)}
        />
      ) : (
        <span className="math-bank-page__chevron-space" />
      )}
      <label className="math-bank-page__check" onPointerDown={startPaint(code)}>
        <input
          type="checkbox"
          checked={isChecked}
          onChange={() => {
            if (skipNextChangeRef.current) {
              skipNextChangeRef.current = false;
              return;
            }
            setChecked(!isChecked);
          }}
        />
        <span>{name}</span>
      </label>
    </div>
    );
  };

  return (
    <div className="math-bank-page__layout math-bank-page__layout--range">
      <section className="math-bank-page__card">
        <div className="math-bank-page__card-head">
          <h4>범위</h4>
        </div>
        {!unitData && <p className="math-bank-page__empty">불러오는 중입니다.</p>}
        {unitData?.error && <p className="math-bank-page__empty">{unitData.error}</p>}
        {levelList.length > 0 && (
          <div className="math-bank-page__subject-table">
            {levelList.map((level) => (
              <div key={level} className="math-bank-page__subject-row">
                <div className="math-bank-page__subject-level">{level}</div>
                <MathBankChipList
                  optionList={subjectList.filter((one) => one.level === level).map((one) => ({ value: one.code, label: one.name }))}
                  isActive={(value) => value === subjectCode}
                  onPick={setSubjectCode}
                />
              </div>
            ))}
          </div>
        )}
        {subject && (
          <div className="math-bank-page__tree">
            {(subject.units || []).map((unit) => (
              <div key={unit.code} className="math-bank-page__tree-group">
                {renderCheckRow({
                  code: unit.code,
                  name: unit.name,
                  isChecked: unitCodeList.includes(unit.code),
                  setChecked: (checked) => setUnitChecked(unit, checked),
                  childCount: (unit.skills || []).length,
                  depth: 0,
                })}
                {openCodeList.includes(unit.code) &&
                  (unit.skills || []).map((skill) => (
                    <React.Fragment key={skill.code}>
                      {renderCheckRow({
                        code: skill.code,
                        name: skill.name,
                        isChecked: skillCodeList.includes(skill.code),
                        setChecked: (checked) => setSkillChecked(unit, skill, checked),
                        childCount: (skill.types || []).length,
                        depth: 1,
                      })}
                      {openCodeList.includes(skill.code) &&
                        (skill.types || []).map((type) => (
                          <React.Fragment key={type.code}>
                            {renderCheckRow({
                              code: type.code,
                              name: type.name,
                              isChecked: typeCodeList.includes(type.code),
                              setChecked: (checked) => setTypeChecked(unit, skill, type, checked),
                              childCount: 0,
                              depth: 2,
                            })}
                          </React.Fragment>
                        ))}
                    </React.Fragment>
                  ))}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="math-bank-page__card math-bank-page__card--sticky">
        <div className="math-bank-page__card-head">
          <h4>조건</h4>
        </div>
        <MathBankField label={`고른 범위 ${selectedCodeList.length}`}>
          {selectedCodeList.length > 0 ? (
            <div className="math-bank-page__selected-list">
              {selectedCodeList.map((code) => (
                <span key={code} className="math-bank-page__selected">
                  <span className="math-bank-page__selected-name">{rangeNameByCode[code]?.name || code}</span>
                  <button
                    type="button"
                    className="math-bank-page__selected-remove"
                    aria-label="빼기"
                    onClick={() => removeRange(code)}
                  >
                    ×
                  </button>
                </span>
              ))}
              <button type="button" className="math-bank-page__text-button" onClick={clearRange}>
                모두 풀기
              </button>
            </div>
          ) : (
            <p className="math-bank-page__placeholder">없음</p>
          )}
        </MathBankField>
        <MathBankFilterFields filter={filter} onChange={onFilterChange} />
        <MathBankField label="난이도 비율">
          <MathBankRatioBar
            label="난이도"
            itemList={DIFFICULTY_LIST.map((difficulty) => ({ key: difficulty, label: difficulty }))}
            ratio={difficultyRatio}
            onChange={setDifficultyRatio}
            total={total}
          />
        </MathBankField>
        {unitCodeList.length > 1 && (
          <MathBankField label="대분류 비율">
            <MathBankRatioBar label="대분류" itemList={ratioItemListOf(unitCodeList)} ratio={shownUnitRatio} onChange={setUnitRatio} total={total} />
          </MathBankField>
        )}
        {skillCodeList.length > 1 && (
          <MathBankField label="소분류 비율">
            <MathBankRatioBar label="소분류" itemList={ratioItemListOf(skillCodeList)} ratio={shownSkillRatio} onChange={setSkillRatio} total={total} />
          </MathBankField>
        )}
        <MathBankField label="형식">
          <MathBankChipList
            optionList={(unitData?.kinds || []).map((one) => ({ value: one.code, label: one.name }))}
            isActive={(value) => kindList.includes(value)}
            onPick={(value) => setKindList((list) => toggleInList(list, value))}
          />
        </MathBankField>
        <MathBankField label="문항 수">
          <input
            type="number"
            className="math-bank-page__number-input"
            min={1}
            max={MAX_TOTAL}
            value={total}
            onChange={(event) => setTotal(Math.max(1, Math.min(MAX_TOTAL, Number(event.target.value) || 1)))}
          />
        </MathBankField>
        <button type="button" className="math-bank-page__primary math-bank-page__primary--wide" onClick={pick} disabled={isBusy}>
          {isBusy ? "뽑는 중" : hasPickedProblems ? "다시 뽑기" : "문제 뽑기"}
        </button>
      </section>
    </div>
  );
};

export default MathBankRangePanel;
